import { ContentItemViewNodesType } from '../../../types'
import { PromoteDemoteActions } from '../constants'
import { PromoteDemoteAction } from '../types'
import { findNodeById, findParentNode } from './nodeHelpers'
import {
    calculateDemoteVariables,
    calculatePromoteVariables,
} from './orderCalculators'

type PartialNode = Partial<ContentItemViewNodesType>

/**
 * Updates a node's parent references based on the new parent
 * @param node - Node to update (will be mutated)
 * @param newParent - New parent node or undefined for root level
 */
const updateNodeParentReferences = (
    node: PartialNode,
    newParent: PartialNode | undefined
): void => {
    if (newParent) {
        node.dtvParentId = newParent.dtvItemId
        node.dtvContentItemParentId = newParent.id
    } else {
        // moving to root level
        node.dtvParentId = undefined
        node.dtvContentItemParentId = undefined
    }
}

/**
 * Finds siblings of a node at a specific parent level
 * @param nodes - All nodes in the tree
 * @param parent - Parent node to find children of (undefined = root level)
 * @param excludeNodeId - ID of node to exclude from results
 * @returns Array of sibling nodes (excluding the specified node)
 */
const findSiblingsAtParent = (
    nodes: PartialNode[],
    parent: PartialNode | undefined,
    excludeNodeId: string
): PartialNode[] => {
    return nodes.filter((node) => {
        if (node.id === excludeNodeId) return false

        const hasParent = parent
            ? node.dtvParentId === parent.dtvItemId ||
              node.dtvContentItemParentId === parent.id
            : !node.dtvParentId && !node.dtvContentItemParentId

        return hasParent
    })
}

/**
 * Adjusts sibling order values when a node is inserted or removed
 * @param siblings - Sibling nodes to adjust
 * @param threshold - Order value threshold for adjustment
 * @param increment - true: shift right (insertion), false: shift left (removal)
 */
const adjustSiblingOrders = (
    siblings: PartialNode[],
    threshold: number,
    increment: boolean
): void => {
    siblings.forEach((sibling) => {
        if (
            sibling.order !== undefined &&
            (increment ? sibling.order >= threshold : sibling.order > threshold)
        ) {
            sibling.order += increment ? 1 : -1
        }
    })
}

/**
 * Calculates new parent and order position based on action type
 * @param action - 'Promote' or 'Demote'
 * @param currentParent - Current parent of the node being moved
 * @param nodeToMove - Node being promoted/demoted
 * @param nodes - All nodes in the tree
 * @param paragraphLevel - Current paragraph level (used for special cases)
 * @returns Object with newParent and order position
 */
const getNewPositionData = (
    action: PromoteDemoteAction,
    currentParent: PartialNode | undefined,
    nodeToMove: PartialNode,
    nodes: PartialNode[],
    paragraphLevel: number
) => {
    return action === PromoteDemoteActions.Promote
        ? calculatePromoteVariables(currentParent, nodes, paragraphLevel)
        : calculateDemoteVariables(nodeToMove, currentParent, nodes)
}

/**
 * Creates an optimistic version of the content tree after a promote/demote operation
 * @param nodes - Current content item view nodes
 * @param contentId - ID of the content being promoted/demoted
 * @param action - Promote or Demote action
 * @param paragraphLevel - Current paragraph level
 * @returns Optimistically updated nodes
 */
export const createOptimisticTreeState = (
    nodes: PartialNode[],
    contentId: string,
    action: PromoteDemoteAction,
    paragraphLevel: number
): PartialNode[] => {
    const nodeToMove = findNodeById(nodes, contentId)
    if (!nodeToMove) return nodes

    const updatedNodes = structuredClone(nodes)
    const clonedNodeToMove = findNodeById(updatedNodes, contentId)!

    // get current and new positions
    const currentParent = findParentNode(updatedNodes, clonedNodeToMove)
    const { newParent, order } = getNewPositionData(
        action,
        currentParent,
        clonedNodeToMove,
        updatedNodes,
        paragraphLevel
    )

    // store original order before moving
    const originalOrder = nodeToMove.order || 0

    // update the moved node
    updateNodeParentReferences(clonedNodeToMove, newParent)
    clonedNodeToMove.order = order

    // adjust orders at new location (shift right for insertion)
    const newLocationSiblings = findSiblingsAtParent(
        updatedNodes,
        newParent,
        contentId
    )
    adjustSiblingOrders(newLocationSiblings, order, true)

    // adjust orders at old location (shift left after removal)
    const oldLocationSiblings = findSiblingsAtParent(
        updatedNodes,
        currentParent,
        contentId
    )
    adjustSiblingOrders(oldLocationSiblings, originalOrder, false)

    return updatedNodes
}
